import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Serial numbers — FR-INV-02.09 / 02.10
 * Individual unit tracking. Left pane lists all serials; right pane shows
 * event history for the selected one.
 */
export function SerialNumbersPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  const [showRecord, setShowRecord] = useState(false);

  const listQuery = useQuery({
    queryKey: ['inv', 'serials', statusFilter],
    queryFn: () => inventoryApi.listSerials({
      status: statusFilter || undefined, limit: 200,
    }),
  });

  const { filtered, kpis } = useMemo(() => {
    const rows = listQuery.data ?? [];
    const lower = search.trim().toLowerCase();
    const filt = lower
      ? rows.filter((s: any) =>
          s.serial?.toLowerCase().includes(lower) ||
          s.product_name?.toLowerCase().includes(lower))
      : rows;
    let inStock = 0, sold = 0, defective = 0;
    for (const s of rows) {
      if (s.status === 'in_stock') inStock++;
      else if (s.status === 'sold') sold++;
      else if (s.status === 'defective') defective++;
    }
    return { filtered: filt, kpis: { total: rows.length, inStock, sold, defective } };
  }, [listQuery.data, search]);

  const STATUS_OPTIONS = [
    { value: '',           label: 'All' },
    { value: 'in_stock',   label: 'In stock' },
    { value: 'sold',       label: 'Sold' },
    { value: 'returned',   label: 'Returned' },
    { value: 'defective',  label: 'Defective' },
    { value: 'scrapped',   label: 'Scrapped' },
    { value: 'in_transit', label: 'In transit' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Serial numbers"
        subtitle="Track individual units from receipt through sale, return, and disposal."
        actions={
          <button className="btn-primary" onClick={() => setShowRecord(true)}>
            + Record serials
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="In stock" value={kpis.inStock} accent="sage" />
        <KpiTile label="Sold" value={kpis.sold} />
        <KpiTile label="Defective" value={kpis.defective}
          accent={kpis.defective > 0 ? 'danger' : 'default'} />
      </div>

      {/* Filter bar */}
      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-48 flex items-center gap-2 px-3">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search by serial or product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0"
          />
        </div>
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={clsx(
                'px-3 py-1 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                statusFilter === s.value
                  ? 'bg-canvas-elevated text-ink-900 shadow-xs'
                  : 'text-ink-600 hover:text-ink-900'
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_28rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? (
            <LoadingState rows={5} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : filtered.length === 0 ? (
            <EmptyState
              message="No serials match"
              hint={search || statusFilter
                ? 'Try a different search or filter.'
                : 'Record the first batch of serials using the button above.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Warehouse</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s: any) => (
                    <tr
                      key={s.id}
                      onClick={() => setSelectedSerial(s.serial)}
                      className={clsx('cursor-pointer', selectedSerial === s.serial && '!bg-canvas-subtle')}
                    >
                      <td className="font-mono text-xs font-semibold text-ink-900">{s.serial}</td>
                      <td className="text-sm text-ink-700">{s.product_name}</td>
                      <td><SerialStatusBadge status={s.status} /></td>
                      <td className="text-xs text-ink-500 font-mono">
                        {s.warehouse_code ?? <span className="text-ink-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selectedSerial ? (
            <EmptyState
              message="Select a serial"
              hint="Click a row on the left to view its full event history."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="4" x2="4" y2="20"/>
                  <line x1="7" y1="4" x2="7" y2="20"/>
                  <line x1="11" y1="4" x2="11" y2="20"/>
                  <line x1="15" y1="4" x2="15" y2="20"/>
                  <line x1="20" y1="4" x2="20" y2="20"/>
                </svg>
              }
            />
          ) : (
            <SerialHistoryView serial={selectedSerial} />
          )}
        </div>
      </div>

      {showRecord && (
        <RecordSerialsModal
          onClose={() => setShowRecord(false)}
          onDone={() => {
            setShowRecord(false);
            listQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

function SerialStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'in_stock'   ? 'sage' :
    status === 'sold'       ? 'info' :
    status === 'returned'   ? 'warning' :
    status === 'defective'  ? 'danger' :
    status === 'scrapped'   ? 'neutral' :
    status === 'in_transit' ? 'info' :
                              'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// SerialHistoryView
// ============================================================
function SerialHistoryView({ serial }: { serial: string }) {
  const historyQuery = useQuery({
    queryKey: ['inv', 'serial', serial],
    queryFn: () => inventoryApi.getSerialHistory(serial),
  });

  if (historyQuery.isLoading) return <LoadingState rows={4} />;
  if (historyQuery.error) return <ErrorState error={historyQuery.error} />;
  const h = historyQuery.data;

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <div className="font-mono text-xs text-ink-500 mb-0.5">{h.serial}</div>
        <div className="font-display font-bold text-lg text-ink-900">{h.product_name}</div>
        <div className="font-mono text-xs text-ink-500 mt-0.5">{h.product_sku}</div>
        <div className="mt-3 flex gap-2 items-center flex-wrap">
          <SerialStatusBadge status={h.status} />
          {h.warehouse_code && (
            <span className="text-xs text-ink-500">
              at <span className="font-mono font-semibold text-ink-700">{h.warehouse_code}</span>
            </span>
          )}
          {h.unit_cost && (
            <span className="text-xs text-ink-500">
              cost <span className="font-mono font-semibold text-ink-700">{fmtCurrency(parseFloat(h.unit_cost))}</span>
            </span>
          )}
        </div>
      </div>

      {/* Event timeline */}
      <div className="eyebrow text-ink-500 mb-3">Event history ({h.events.length})</div>
      <ul className="relative space-y-4 pl-6">
        <div className="absolute left-[5px] top-2 bottom-2 w-px bg-ink-200" />
        {h.events.map((e: any) => (
          <li key={e.id} className="relative">
            <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-sage-500 ring-2 ring-canvas-elevated" />
            <div className="eyebrow text-ink-900 text-2xs">{e.event_type.replace('_', ' ')}</div>
            <div className="text-xs text-ink-500 mt-0.5 font-mono">
              {new Date(e.created_at).toLocaleString()}
              {e.performed_by_name && <span className="text-ink-400"> · {e.performed_by_name}</span>}
            </div>
            {e.from_status && (
              <div className="text-xs text-ink-600 mt-1">
                <span className="text-ink-400">{e.from_status.replace('_', ' ')}</span>
                <span className="mx-1 text-ink-400">→</span>
                <span className="font-medium text-ink-900">{e.to_status.replace('_', ' ')}</span>
              </div>
            )}
            {e.notes && (
              <div className="text-xs text-ink-600 italic mt-1 bg-canvas-subtle rounded-md px-2 py-1">
                {e.notes}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================
// RecordSerialsModal
// ============================================================
function RecordSerialsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [serialsText, setSerialsText] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [productSearch, setProductSearch] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const prodQuery = useQuery({
    queryKey: ['products-serial-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const serials = serialsText.split(/\s+/).map((s) => s.trim()).filter(Boolean);

  const mut = useMutation({
    mutationFn: () => inventoryApi.recordSerials({
      productId,
      warehouseId,
      serials,
      unitCost: unitCost ? parseFloat(unitCost) : undefined,
    }),
    onSuccess: (data) => {
      toast.success(`${data.count} serials recorded`);
      qc.invalidateQueries({ queryKey: ['inv', 'serials'] });
      onDone();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const selectedProduct = productId ? prodQuery.data?.data?.find((p: any) => p.id === productId) : null;
  const canSubmit = productId && warehouseId && serials.length > 0;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Record serial numbers</h2>
        <p className="text-sm text-ink-500 mt-1">
          Bulk-add serial numbers for a serial-tracked product. Useful for migrating legacy stock or manual GRN outside the purchase flow.
        </p>
      </div>

      <div className="px-6 pb-5 flex-1 overflow-y-auto space-y-4">
        <div>
          <label className="label">Product <span className="font-normal text-ink-500 normal-case tracking-normal">(must be serial-tracked)</span></label>
          {selectedProduct ? (
            <div className="flex gap-2 items-center bg-canvas-subtle rounded-xl px-3 py-2.5">
              <span className="font-mono text-xs font-semibold text-ink-900">{selectedProduct.sku}</span>
              <span className="flex-1 font-medium text-ink-900 truncate">{selectedProduct.name}</span>
              <button
                className="text-xs font-medium text-ink-500 hover:text-ink-900 transition-colors"
                onClick={() => { setProductId(''); setProductSearch(''); }}
              >
                Change
              </button>
            </div>
          ) : (
            <div>
              <input
                className="input text-sm"
                placeholder="Search product…"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
              {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-ink-100 rounded-xl">
                  {(prodQuery.data?.data ?? []).map((p: any) => (
                    <button
                      key={p.id}
                      className={clsx(
                        'w-full text-left px-3 py-2 text-xs transition-colors border-b border-ink-100 last:border-0',
                        p.is_serial_tracked
                          ? 'hover:bg-canvas-subtle/50'
                          : 'opacity-50 cursor-not-allowed'
                      )}
                      onClick={() => p.is_serial_tracked && setProductId(p.id)}
                      disabled={!p.is_serial_tracked}
                    >
                      <span className="font-mono text-ink-700">{p.sku}</span>
                      <span className="text-ink-900 ml-2">{p.name}</span>
                      {!p.is_serial_tracked && (
                        <span className="text-danger text-2xs ml-2">not tracked</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-[1fr_140px] gap-3">
          <div>
            <label className="label">Warehouse</label>
            <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">Select…</option>
              {whQuery.data?.map((w: any) => (
                <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Unit cost</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm pointer-events-none">$</span>
              <input
                type="number" step="0.01" min="0"
                className="input pl-6 tabular-nums"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="label">Serials <span className="font-normal text-ink-500 normal-case tracking-normal">— one per line or space-separated</span></label>
          <textarea
            className="input font-mono text-sm"
            rows={8}
            value={serialsText}
            onChange={(e) => setSerialsText(e.target.value)}
            placeholder={'SN-001\nSN-002\nSN-003'}
          />
          <div className="text-2xs text-ink-500 mt-1.5 font-mono tabular-nums">
            {serials.length} serial{serials.length === 1 ? '' : 's'} parsed
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex-shrink-0 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!canSubmit || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Recording…' : `Record ${serials.length || ''}`}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function SearchIcon() { return <svg {...iconProps} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
